// ============================================================
// v11 §2G (BOLT-027) — the live cut preview, built ONLY from the
// shared cut geometry (cutGeometry.ts) the server resolves with:
// preview ≡ resolution by construction. Inputs are PUBLIC — the
// seeker's own pin, card params, the zone, the boundary — never H,
// and never which side survives: candidate sections only.
// ============================================================
import { featureCollection, intersect, area } from '@turf/turf';
import type { Feature, LineString, Polygon, MultiPolygon, Position } from 'geojson';
import { simplifyZone } from '../../shared/engine';
import {
  clipLineToBoundary,
  directionWedges,
  quadrantRegions,
  radarDisk,
  sliceHalfPlanes,
  WEDGE_START,
} from '../../shared/cutGeometry';
import type { Boundary } from '../../shared/protocol';
import { destination, point } from '@turf/turf';
import { ft } from '../../shared/constants';
import type { CardMenuItem, LngLat, PublicState, Zone } from '../../shared/protocol';
import { LANDMARK_TAG_FOR_CARD } from '../../shared/landmarkTags'; // v24 BOLT-130

export interface CutPreview {
  /** Stable identity — re-render only when this changes. */
  key: string;
  /** Candidate regions clipped to the live zone, in a stable order. Rendered
   *  with alternating non-committal tints (never "this side survives"). */
  sections: Zone[];
  /** Dashed cut lines (disk edge / X radials / cross / bisector / contour). */
  lines: Feature<LineString>[];
}

// The EXACT composition the resolvers use (simplifyZone ∘ intersect), so a
// previewed section is byte-identical to the zone the engine would produce
// if that section wins — asserted by the parity fixtures (cutPreview.test.ts).
const clipToZone = (regions: Feature<Polygon | MultiPolygon>[], zone: Zone): Zone[] => {
  const out: Zone[] = [];
  for (const r of regions) {
    try {
      const c = intersect(featureCollection([zone as Feature<Polygon | MultiPolygon>, r]));
      if (c && area(c) > 1) out.push(simplifyZone(c) as Zone);
    } catch {
      /* degenerate — skip the section */
    }
  }
  return out;
};

const radial = (S: LngLat, bearingDeg: number, lenM = 8000): Feature<LineString> => {
  const d = destination(point([S.lng, S.lat]), lenM, bearingDeg, { units: 'meters' });
  return {
    type: 'Feature',
    properties: {},
    geometry: { type: 'LineString', coordinates: [[S.lng, S.lat], d.geometry.coordinates as Position] },
  };
};

const outlines = (f: Feature<Polygon | MultiPolygon>): Feature<LineString>[] => {
  const rings = f.geometry.type === 'Polygon' ? f.geometry.coordinates : f.geometry.coordinates.flat();
  return rings.map((ring) => ({ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: ring } }));
};

// v13 BOLT-037: preview LINES never span outside the play area (Jake's rule;
// the section FILLS were already zone-clipped). No boundary ⇒ pass through.
const clipLines = (lines: Feature<LineString>[], boundary: Boundary | null | undefined): Feature<LineString>[] => {
  if (!boundary) return lines;
  return lines.flatMap((l) =>
    clipLineToBoundary(l.geometry.coordinates as Position[], boundary).map(
      (seg): Feature<LineString> => ({ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: seg } }),
    ),
  );
};

// v14 BOLT-045 / v21 BOLT-101: the synthetic Slice cut — a 1 m displacement from
// S at the caller's bearing stands in for the not-yet-walked start→stop pair, so
// the SELECTION preview shows the slicing mechanic (a cut ALONG the walk), not a
// walk-radius ring. Same public inputs as everything here (S, zone, boundary —
// never H); the bearing is random AT THE CALLER (stable per selection).
const SYNTHETIC_THERMO_M = 1;
const syntheticThermoCut = (
  S: LngLat,
  bearingDeg: number,
  zone: Zone,
  boundary: Boundary | null | undefined,
): Pick<CutPreview, 'sections' | 'lines'> => {
  const d = destination(point([S.lng, S.lat]), SYNTHETIC_THERMO_M, bearingDeg, { units: 'meters' });
  const end: LngLat = { lat: d.geometry.coordinates[1], lng: d.geometry.coordinates[0] };
  const halves = sliceHalfPlanes(S, end);
  // the two along-walk vertices ARE the slice line (through S, extended both ways)
  const sliceLine = halves[0].geometry.coordinates[0].slice(0, 2) as Position[];
  return {
    sections: clipToZone(halves, zone),
    lines: clipLines(
      [{ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: sliceLine } }],
      boundary,
    ),
  };
};

/** The preview for one filtering card (selected or in its veto window), or for
 *  the live thermometer walk. Null ⇒ nothing to preview (clear the layers).
 *  `syntheticThermoBearingDeg` (BOLT-045): the stand-in walk direction for a
 *  thermometer with no real displacement yet — random per selection at the
 *  caller, deterministic here. */
export function buildCutPreview(
  state: PublicState | null,
  selected: CardMenuItem | null,
  syntheticThermoBearingDeg = 90,
): CutPreview | null {
  if (!state || state.you.role !== 'seeker' || !state.zone) return null;
  const zone = state.zone;
  const pin = state.selfPin ?? state.seekerPin;
  if (!pin) return null;
  const S: LngLat = { lat: pin.lat, lng: pin.lng };

  // ---- the live thermometer walk: the EXACT eventual cut, updating live ----
  if (state.thermo) {
    const start = state.thermo.startPin;
    if (Math.abs(start.lat - S.lat) < 1e-9 && Math.abs(start.lng - S.lng) < 1e-9) {
      // no displacement yet — show the synthetic cut until the walk begins
      // (v14 BOLT-045; the ring told you the walk radius, not the mechanic)
      return {
        key: `thermo-wait:${state.thermo.cardId}:${syntheticThermoBearingDeg.toFixed(1)}`,
        ...syntheticThermoCut(start, syntheticThermoBearingDeg, zone, state.boundary),
      };
    }
    const halves = sliceHalfPlanes(start, S);
    const sliceLine = halves[0].geometry.coordinates[0].slice(0, 2) as Position[];
    return {
      key: `thermo:${state.thermo.cardId}:${S.lat.toFixed(6)},${S.lng.toFixed(6)}`,
      sections: clipToZone(halves, zone),
      lines: clipLines(
        [{ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: sliceLine } }],
        state.boundary,
      ),
    };
  }

  // ---- a card selected (pre-play) or awaiting its veto window ----
  const pending = state.pendingPlays.find((p) => (state.deck ?? []).some((c) => c.id === p.cardId));
  const card = selected ?? (pending ? (state.deck ?? []).find((c) => c.id === pending.cardId) ?? null : null);
  if (!card || card.script) return null;

  if (card.family === 'radar' && card.radiusFt) {
    const disk = radarDisk(S, ft(card.radiusFt));
    return {
      key: `radar:${card.id}:${S.lat.toFixed(6)},${S.lng.toFixed(6)}`,
      sections: clipToZone([disk], zone), // in-section; the out-section is the rest of the zone
      lines: clipLines(outlines(disk), state.boundary),
    };
  }
  if (card.id === 'quadrants') {
    // v11 §2K: the axis-aligned cross — four quadrant sections
    return {
      key: `quadrants:${S.lat.toFixed(6)},${S.lng.toFixed(6)}`,
      sections: clipToZone(quadrantRegions(S).map((q) => q.region), zone),
      lines: clipLines([0, 90, 180, 270].map((b) => radial(S, b)), state.boundary),
    };
  }
  if (card.id === 'borders') {
    // v20 BOLT-099: the preview is the SERVER's `bordersRegions` (the exact
    // resolution math), sent on the seeker's state — a single source of truth,
    // faithful by construction, and identical geometry on web + iOS.
    const regions = state.bordersPreview;
    if (!regions) return null;
    return {
      key: `borders:${state.pctRemaining.toFixed(2)}`,
      sections: clipToZone([regions.band, regions.core], zone),
      lines: outlines(regions.core),
    };
  }
  // v24 BOLT-130 (Jake: "we want a preview so that the seeker can see the
  // variety of zones that the map would be split into"). Same pattern as
  // Borders: the SERVER computed these cells and sent them, so the preview is
  // the resolution math by construction, and neither client runs a Voronoi.
  if (card.family === 'landmark') {
    const tag = LANDMARK_TAG_FOR_CARD[card.id];
    const cells = tag ? state.featureCells?.[tag] : undefined;
    if (!cells?.length) return null;
    return {
      key: `landmark:${card.id}:${cells.length}:${state.pctRemaining.toFixed(2)}`,
      sections: clipToZone(cells.map((c) => c.region), zone),
      // Outline every cell, so the split reads as a set of candidate regions.
      lines: cells.flatMap((c) => outlines(c.region)),
    };
  }
  if (card.family === 'direction') {
    // the true X-cut (diagonal wedges), NOT axis-aligned quadrants (§2K)
    return {
      key: `direction:${S.lat.toFixed(6)},${S.lng.toFixed(6)}`,
      sections: clipToZone(directionWedges(S).map((w) => w.region), zone),
      lines: clipLines(Object.values(WEDGE_START).map((b) => radial(S, b)), state.boundary),
    };
  }
  if (card.family === 'thermometer' && card.minFt) {
    // v14 BOLT-045: at selection, preview the CUT — the synthetic 1 m pair
    // stands in for start→stop until the real walk begins (live branch above).
    // The min-walk number lives in ThermoHud; the ring is gone.
    return {
      key: `thermo-syn:${card.id}:${syntheticThermoBearingDeg.toFixed(1)}:${S.lat.toFixed(6)},${S.lng.toFixed(6)}`,
      ...syntheticThermoCut(S, syntheticThermoBearingDeg, zone, state.boundary),
    };
  }
  return null; // photo cards etc. — nothing geometric to preview
}
